import { useState } from 'react';
import { Link } from 'react-router-dom';
import { communityPosts } from '@/mocks/community';

const roleColors: Record<string, string> = {
  student: 'bg-blue-100 text-blue-700',
  teacher: 'bg-green-100 text-green-700',
  parent: 'bg-amber-100 text-amber-700',
};
const roleLabels: Record<string, string> = {
  student: '学生',
  teacher: '教师',
  parent: '家长',
};

type Season = 'spring' | 'summer' | 'autumn' | 'winter';

const SEASON_CONFIG: Record<Season, { label: string; emoji: string; gradFrom: string; gradTo: string; bg: string }> = {
  spring: { label: '春季', emoji: '🌸', gradFrom: '#ec4899', gradTo: '#f43f5e', bg: 'linear-gradient(135deg,#7B5E3A,#9B7A52,#7B5E3A)' },
  summer: { label: '夏季', emoji: '☀️', gradFrom: '#f59e0b', gradTo: '#f97316', bg: 'linear-gradient(135deg,#6B4E1A,#8B6A2A,#6B4E1A)' },
  autumn: { label: '秋季', emoji: '🍂', gradFrom: '#f97316', gradTo: '#f59e0b', bg: 'linear-gradient(135deg,#8B5E2A,#A0723A,#8B5E2A)' },
  winter: { label: '冬季', emoji: '❄️', gradFrom: '#38bdf8', gradTo: '#06b6d4', bg: 'linear-gradient(135deg,#5A6E7A,#7A8E9A,#5A6E7A)' },
};

const SEASON_CROPS: Record<Season, { emoji: string; name: string; score: number }[]> = {
  spring: [
    { emoji: '🥕', name: '胡萝卜', score: 0 },
    { emoji: '🍓', name: '草莓', score: 100 },
    { emoji: '🍒', name: '樱桃', score: 200 },
    { emoji: '🫛', name: '豌豆', score: 50 },
  ],
  summer: [
    { emoji: '🍉', name: '西瓜', score: 150 },
    { emoji: '🌽', name: '玉米', score: 100 },
    { emoji: '🍅', name: '番茄', score: 80 },
    { emoji: '🍑', name: '桃子', score: 300 },
  ],
  autumn: [
    { emoji: '🎃', name: '南瓜', score: 120 },
    { emoji: '🍇', name: '葡萄', score: 250 },
    { emoji: '🍎', name: '苹果', score: 200 },
    { emoji: '🍄', name: '蘑菇', score: 180 },
  ],
  winter: [
    { emoji: '🥬', name: '白菜', score: 0 },
    { emoji: '🌼', name: '雪莲花', score: 400 },
    { emoji: '🌲', name: '松果', score: 500 },
    { emoji: '🫚', name: '萝卜', score: 60 },
  ],
};

const getCurrentSeason = (): Season => {
  const m = new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring';
  if (m >= 6 && m <= 8) return 'summer';
  if (m >= 9 && m <= 11) return 'autumn';
  return 'winter';
};

const DEMO_PLOTS = [
  { emoji: '🥕', stage: 4, watered: true },
  { emoji: '🌿', stage: 2, watered: false },
  { emoji: '🍓', stage: 4, watered: true },
  { emoji: '🌱', stage: 1, watered: false },
  { emoji: '🟫', stage: 0, watered: false },
  { emoji: '🌾', stage: 3, watered: true },
  { emoji: '🟫', stage: 0, watered: false },
  { emoji: '🌿', stage: 2, watered: true },
  { emoji: '🟫', stage: 0, watered: false },
];

export default function CommunityPreviewSection() {
  const [currentSeason] = useState<Season>(getCurrentSeason());
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [demoPlots, setDemoPlots] = useState(DEMO_PLOTS);
  const [farmNotif, setFarmNotif] = useState<string | null>(null);

  const seasonInfo = SEASON_CONFIG[currentSeason];
  const seasonCrops = SEASON_CROPS[currentSeason];

  const toggleLike = (id: string) => {
    setLikedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleDemoPlotClick = (idx: number) => {
    const plot = demoPlots[idx];
    if (plot.stage === 4) {
      setDemoPlots(prev => prev.map((p, i) => i === idx ? { ...p, stage: 0, emoji: '🟫', watered: false } : p));
      setFarmNotif('收获成功！注册后解锁完整农场游戏 🎉');
    } else if (plot.stage > 0 && !plot.watered) {
      setDemoPlots(prev => prev.map((p, i) => i === idx ? { ...p, watered: true } : p));
      setFarmNotif('浇水成功！注册后体验完整功能 💧');
    } else if (plot.stage === 0) {
      setFarmNotif('注册后即可种植蔬菜！🌱');
    }
    setTimeout(() => setFarmNotif(null), 2000);
  };

  return (
    <section className="py-20 bg-[#faf9f7]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-green-100 rounded-full px-3 py-1 mb-3">
              <i className="ri-community-line text-green-600 text-xs"></i>
              <span className="text-green-700 text-xs font-semibold">互动交流社区</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">城乡互助 · 学习农场</h2>
            <p className="text-gray-500 mt-2 text-sm">多学习赚积分，积分越多农场蔬菜越丰盛！</p>
          </div>
          <Link
            to="/community"
            className="flex items-center gap-2 text-green-600 font-semibold text-sm hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
          >
            进入社区 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Farm Game Preview (3 cols) */}
          <div className="lg:col-span-3 flex flex-col gap-4">
            {/* Farm Card */}
            <div className="rounded-3xl overflow-hidden border border-gray-200">
              {/* Season Header */}
              <div
                className="p-5 text-white relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${seasonInfo.gradFrom}, ${seasonInfo.gradTo})` }}
              >
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-8xl opacity-20">{seasonInfo.emoji}</div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{seasonInfo.emoji}</span>
                    <div>
                      <p className="font-black text-lg">学习农场 · {seasonInfo.label}</p>
                      <p className="text-white/80 text-xs">多学习 = 蔬菜成长更快，收获更多！</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {seasonCrops.map((crop) => (
                      <div key={crop.name} className="flex items-center gap-1 bg-white/20 rounded-full px-2.5 py-1">
                        <span className="text-sm">{crop.emoji}</span>
                        <span className="text-xs font-semibold">{crop.name}</span>
                        {crop.score > 0 && <span className="text-white/70 text-[10px]">{crop.score}分解锁</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Interactive Demo Farm */}
              <div className="bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold text-gray-800">试玩农场（点击互动！）</p>
                  <Link to="/community" className="text-xs text-green-600 font-semibold hover:underline cursor-pointer">注册解锁完整版 →</Link>
                </div>

                {farmNotif && (
                  <div className="mb-3 bg-green-50 rounded-xl px-3 py-2 flex items-center gap-2 text-green-700 text-xs font-medium">
                    <i className="ri-checkbox-circle-line text-green-500"></i>
                    {farmNotif}
                  </div>
                )}

                <div className="rounded-2xl p-3 grid grid-cols-3 gap-2 mb-4" style={{ background: seasonInfo.bg }}>
                  {demoPlots.map((plot, idx) => {
                    const isReady = plot.stage === 4;
                    const isEmpty = plot.stage === 0;
                    const needsWater = plot.stage > 0 && plot.stage < 4 && !plot.watered;
                    return (
                      <button
                        key={idx}
                        onClick={() => handleDemoPlotClick(idx)}
                        className={`relative aspect-square rounded-xl flex flex-col items-center justify-center gap-1 transition-all cursor-pointer border-2 ${
                          isReady ? 'bg-yellow-100 border-yellow-400 animate-pulse' :
                          isEmpty ? 'bg-amber-100/60 border-amber-300/50 hover:bg-amber-100' :
                          needsWater ? 'bg-green-100 border-orange-300 hover:border-orange-400' :
                          'bg-green-100 border-green-300'
                        }`}
                      >
                        {isReady && <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center"><i className="ri-star-fill text-white text-[8px]"></i></div>}
                        {needsWater && <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full flex items-center justify-center"><span className="text-[8px]">💧</span></div>}
                        <span className="text-2xl leading-none">{plot.emoji}</span>
                        <span className="text-[9px] text-gray-600 font-medium">
                          {isEmpty ? '空地' : isReady ? '点击收获' : needsWater ? '点击浇水' : '成长中'}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* How it works */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { icon: 'ri-book-open-line', label: '学习赚积分', color: 'text-green-600', bg: 'bg-green-50' },
                    { icon: 'ri-plant-line', label: '积分种蔬菜', color: 'text-amber-600', bg: 'bg-amber-50' },
                    { icon: 'ri-gift-line', label: '收获换奖励', color: 'text-rose-600', bg: 'bg-rose-50' },
                  ].map((item) => (
                    <div key={item.label} className={`${item.bg} rounded-xl p-3 text-center`}>
                      <div className={`w-8 h-8 flex items-center justify-center mx-auto mb-1.5 bg-white rounded-full`}>
                        <i className={`${item.icon} ${item.color} text-sm`}></i>
                      </div>
                      <p className={`text-xs font-semibold ${item.color}`}>{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Season Switcher Preview */}
            <div className="grid grid-cols-4 gap-2">
              {(Object.keys(SEASON_CONFIG) as Season[]).map((s) => {
                const info = SEASON_CONFIG[s];
                const crops = SEASON_CROPS[s];
                return (
                  <div
                    key={s}
                    className={`rounded-2xl p-3 text-center border-2 transition-all ${
                      s === currentSeason ? 'border-green-400 bg-green-50' : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{info.emoji}</span>
                    <p className="text-xs font-bold text-gray-800 mb-1">{info.label}</p>
                    <div className="flex justify-center gap-0.5 flex-wrap">
                      {crops.slice(0, 2).map(c => <span key={c.name} className="text-sm">{c.emoji}</span>)}
                    </div>
                    {s === currentSeason && <p className="text-[10px] text-green-600 font-semibold mt-1">当前季节</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Community Posts + Channels (2 cols) */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Posts */}
            <div className="flex flex-col gap-3">
              {communityPosts.slice(0, 3).map((post) => (
                <div key={post.id} className="bg-white rounded-2xl p-4 hover:scale-[1.01] transition-all cursor-pointer border border-gray-100">
                  <div className="flex items-start gap-3 mb-3">
                    <img src={post.avatar} alt={post.author} className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-gray-900 text-xs">{post.author}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${roleColors[post.role]}`}>{roleLabels[post.role]}</span>
                      </div>
                      <p className="text-gray-400 text-[10px] mt-0.5">{post.time}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 text-xs leading-relaxed mb-2 line-clamp-2">{post.content}</p>
                  <div className="flex items-center gap-4 text-gray-400 text-[10px]">
                    <button
                      onClick={() => toggleLike(post.id)}
                      className={`flex items-center gap-1 cursor-pointer transition-colors ${likedIds.has(post.id) ? 'text-rose-500' : 'hover:text-rose-500'}`}
                    >
                      <i className={`text-sm ${likedIds.has(post.id) ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                      {post.likes + (likedIds.has(post.id) ? 1 : 0)}
                    </button>
                    <span className="flex items-center gap-1"><i className="ri-chat-1-line text-sm"></i> {post.comments}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Community Channels */}
            <div className="flex flex-col gap-2">
              {[
                { icon: 'ri-user-heart-line', title: '学生互助', desc: '城乡学生配对', link: '/community/student', color: 'from-blue-500 to-blue-600' },
                { icon: 'ri-user-star-line', title: '师生答疑', desc: '名师免费辅导', link: '/community/teacher', color: 'from-green-500 to-green-600' },
                { icon: 'ri-heart-line', title: '爱心捐助', desc: '文具送农村', link: '/donate', color: 'from-rose-500 to-pink-500' },
              ].map((ch) => (
                <Link
                  key={ch.title}
                  to={ch.link}
                  className={`bg-gradient-to-r ${ch.color} rounded-xl p-3 text-white cursor-pointer hover:scale-[1.01] transition-all flex items-center gap-3`}
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className={`${ch.icon} text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{ch.title}</p>
                    <p className="text-white/80 text-xs">{ch.desc}</p>
                  </div>
                  <i className="ri-arrow-right-line text-white/70"></i>
                </Link>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/community"
              className="w-full py-3.5 bg-green-600 text-white font-bold rounded-2xl cursor-pointer hover:bg-green-700 transition-all text-center text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-plant-line"></i>
              注册加入，开始种植你的学习农场
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
